import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useReselector } from '@/utils/sharedHooks'
import {
  getGroupedActiveTaxEstimatesWithinActiveQuarter,
  selectUserCreatedPastQteCutoff,
} from '../userTaxEstimates.selector'
import {
  selectCalculationHomeState,
  selectUserTaxCalculationWithinActiveQuarter,
} from '@/selectors/userTaxCalculation.selectors'
import { selectIsInUnservicedFreeTrial } from '@/selectors/user.selectors'

import {
  ActiveQuarterTaxSummary,
  LastQuarterTaxSummary,
} from './SingleQuarterTaxSummary'

import { QTETimeline } from './QTETimeline'
import { QTEFAQs } from './QTEFAQs'
import { AskHavenAboutEstimate } from './AskHavenAboutEstimate'
import { QTEChecklist } from './qte-checklist'
import { BreakdownBanner } from './BreakdownBanner'
import { UserJoinedTooLate } from './calculate-safe-harbor-flow/UserJoinedTooLate'
import { BasicPlanQTEUpsell } from '../BasicPlanUpsell'
import { ProductFeatureId } from '@/actions/permissionActions'

import {
  selectActiveQTEDetailsAsDates,
  selectIsSameOrAfterChecklistLaunchDate,
} from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'
import { UserTaxCalculationStatuses } from '@/features/admin/user-tax-calculations/userTaxCalculation.slice'
import { selectUserHasPermission } from '@/reducers/auth/permission.selectors'

import { UpdateQuarterlyTaxEstimateDialog } from './UpdateQuarterlyTaxEstimateDialog'
import { TypographyStyles } from '@/styles'
import { Button, Link, Text, HeroCard, Card } from '@/ui'

export const ActiveQuarterTab = () => {
  const navigate = useNavigate()
  const activeQuarterDetails = useReselector(selectActiveQTEDetailsAsDates)
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)
  const hasQTEPermission = useReselector(
    selectUserHasPermission,
    ProductFeatureId.quarterlyTaxes
  )
  const activeQuarterText = `Q${activeQuarterDetails?.taxQuarter} ${activeQuarterDetails?.taxYear}`
  const [estimateModalOpen, setEstimateModalOpen] = useState(false)
  const userJoinedTooLate = useReselector(selectUserCreatedPastQteCutoff)
  const calculation = useReselector(selectUserTaxCalculationWithinActiveQuarter)
  const homeState = useReselector(selectCalculationHomeState, calculation?.id)

  const { federalTaxEstimate } = useReselector(
    getGroupedActiveTaxEstimatesWithinActiveQuarter,
    homeState
  )

  const estimatesReleased =
    calculation?.status === UserTaxCalculationStatuses.savedAndNotified

  const afterChecklistLaunchDate = useReselector(
    selectIsSameOrAfterChecklistLaunchDate
  )

  const mainContent = useMemo(() => {
    if (!hasQTEPermission) {
      return <BasicPlanQTEUpsell />
    }
    if (isInUnservicedFreeTrial) {
      return (
        <HeroCard
          title="Preview of the Quarterly Tax Estimate Experience"
          imageSrc="https://heard-images.s3.amazonaws.com/assets/coins-stack.svg"
          imageAlt="Coins Stack"
          description="Every quarter, we calculate your estimated federal and state payments and even remind you when to pay so you never miss a deadline."
        >
          <Text variant={TypographyStyles.BODY_MD}>
            We have shown an example of this process in the section below — note
            that you will not be able to interact with it.
          </Text>

          <Link
            newPage
            href="https://support.joinheard.com/hc/en-us/articles/4416208116247"
            variant="secondary"
          >
            Learn how Heard prepares your Quarterly Tax Estimates
          </Link>

          <Button
            variant="primary"
            onClick={() => navigate('/accounts/choose-plan')}
          >
            Need help with Taxes? Sign up with Heard
          </Button>
          <div className="opacity-50 pointer-events-none">
            <QTEChecklist />
          </div>
        </HeroCard>
      )
    }

    if (userJoinedTooLate) {
      return (
        <UserJoinedTooLate
          activeQuarterText={activeQuarterText}
          federalTaxEstimate={federalTaxEstimate}
        />
      )
    }

    if (!afterChecklistLaunchDate) {
      return <LastQuarterTaxSummary />
    }

    if (!estimatesReleased || calculation?.needsRecalculation) {
      return <QTEChecklist />
    }

    return <ActiveQuarterTaxSummary quarterText={activeQuarterText} />
  }, [
    userJoinedTooLate,
    afterChecklistLaunchDate,
    estimatesReleased,
    calculation?.needsRecalculation,
    activeQuarterText,
    federalTaxEstimate,
    isInUnservicedFreeTrial,
    hasQTEPermission,
    navigate,
  ])

  return (
    <Card className="space-y-6 p-6">
      <BreakdownBanner />

      <div className="flex flex-col lg:flex-row lg:gap-6">
        {/* Left Column */}
        <div className="w-full lg:w-[68%]">{mainContent}</div>

        {/* Right Column */}
        <div className="w-full lg:w-[32%] mt-6 lg:mt-0">
          {!userJoinedTooLate && <QTETimeline />}
          <AskHavenAboutEstimate
            hasEstimate={
              estimatesReleased || Boolean(federalTaxEstimate?.safeHarborType)
            }
          />
          <QTEFAQs />
        </div>
      </div>

      {federalTaxEstimate && (
        <UpdateQuarterlyTaxEstimateDialog
          open={estimateModalOpen}
          close={() => setEstimateModalOpen(false)}
          estimate={federalTaxEstimate}
        />
      )}
    </Card>
  )
}
